import { useState } from "react";
import { Check, Copy } from "lucide-react";
import { copyText, cx } from "../../utils";

/**
 * The app's one-click values (Apps.copy_buttons) — things the installer has to
 * paste into their provider account during setup.
 *
 * Each entry is { title, description, copy_url }: the title labels the button,
 * the description is its tooltip, and copy_url is what lands on the clipboard.
 *
 * copy_url may contain {{appId}}, {{locationId}} or {{companyId}}. Inbound
 * webhooks are per-install, so the admin authors one templated URL and each
 * account copies its own.
 */
const fill = (url = "", context = {}) =>
  url.replace(
    /\{\{\s*(appId|locationId|companyId)\s*\}\}/g,
    (_, key) => context[key] || "",
  );

const CopyButtonRow = ({ button, context }) => {
  const value = fill(button.copy_url, context);
  const [copied, setCopied] = useState(false);

  const onCopy = async () => {
    if (!value) return;
    if (await copyText(value)) {
      setCopied(true);
      setTimeout(() => setCopied(false), 1600);
    }
  };

  return (
    <button
      type="button"
      onClick={onCopy}
      title={button.description || value}
      className={cx(
        "group flex w-full items-center gap-3 rounded-lg border px-3.5 py-2.5 text-left transition-colors",
        copied
          ? "border-emerald-200 bg-emerald-50"
          : "border-ink-200 bg-white hover:border-ink-300 hover:bg-ink-50",
      )}
    >
      <span className="min-w-0 flex-1">
        <span
          className={cx(
            "block truncate text-[13px] font-medium",
            copied ? "text-emerald-800" : "text-ink-800",
          )}
        >
          {button.title}
        </span>
        {button.description && (
          <span className="mt-0.5 block truncate text-[11px] leading-snug text-ink-500">
            {button.description}
          </span>
        )}
      </span>

      <span
        className={cx(
          "grid h-7 w-7 shrink-0 place-items-center rounded-md transition-colors",
          copied
            ? "text-emerald-600"
            : "text-ink-400 group-hover:bg-ink-200/60 group-hover:text-ink-700",
        )}
        aria-hidden
      >
        {copied ? <Check size={14} /> : <Copy size={14} />}
      </span>

      <span className="sr-only">
        {copied ? "Copied" : `Copy ${button.title}`}
      </span>
    </button>
  );
};

const CopyButtons = ({ buttons = [], context = {} }) => {
  // Entries saved before both fields were required would render as a button
  // that copies nothing.
  const usable = buttons.filter((b) => b?.title && b?.copy_url);

  if (usable.length === 0) return null;

  return (
    <div className="space-y-2">
      {usable.map((button, i) => (
        <CopyButtonRow
          key={button._id || `${button.title}-${i}`}
          button={button}
          context={context}
        />
      ))}
    </div>
  );
};

export default CopyButtons;
